<template>
    <b-overlay
        variant="white"
        :show="showLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <RequestLifeCycle
            ref="requestLifeCycle"
            :life-cycle="request.lifeCycle"
        />
        <b-card ref="verticalStepComponent" id="simple-flow">
            <b-row class="mb-1">
                <b-col md="4" class="d-flex align-items-center">
                    <span class="align-middle">
                        <StatusBadge
                            type="received"
                            :status="request.status"
                        />
                    </span>
                </b-col>
                <b-col md="8">
                    <Actions
                        :request="request"
                        :show-save="isSaveButtonVisible"
                        :is-reply-disabled="isReplyDisabled"
                        :show-reply="isReplyButtonVisible"
                        :show-forward="false"
                        :is-life-cycle-visible="request.lifeCycle.length > 0"
                        @showLifeCycle="showLifeCycle"
                        @close="close"
                        class="float-right"
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <h2 class="text-center">
                        {{ $t('Page(ReceivedRequest):Component(Text):page-label', {customer: request.customer.title, product: getProductTitle()}) }}
                    </h2>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <ViewRequest
                        :request="request"
                    />
                </b-col>
            </b-row>

            <h4 class="reply-process-heading">{{ $t('Page(ReceivedRequest):Component(Text):reply-process') }}</h4>

            <app-collapse
                type="margin"
                class="vertical-steps-flow"
            >
                <app-collapse-item
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-general-information-label')"
                    :header-bg-variant="firstStepStyle.bg"
                    :header-text-variant="firstStepStyle.text"
                    :class="firstStepStyle.class"
                    is-visible
                    ref="step1"
                >
                    <Overlay :show="productInformationLoading">
                        <ProductInformation
                        ref="ProductInformation"
                        class="mt-1"
                        :product="product"
                        :request="request"
                        :customer-product="request.customerProduct"
                        @validityChanged="(isValid) => changeStepValidity(1, isValid)"
                        @change="productInformationChanged"
                    />
                    </Overlay>
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            :disabled="!isStepValid(1)"
                            @click="() => doneStep(1)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible('ce')"
                    :title="$t('Page(ReceivedRequestFMDFlow):Component(StepItem):step-concern-elements-label')"
                    :class="getStepVariantStyles(2).class"
                    :header-bg-variant="getStepVariantStyles(2).bg"
                    :header-text-variant="getStepVariantStyles(2).text"
                    ref="step2"
                >
                    <InstructionText class="simple-flow-instruction">
                        {{ $t('Page(ReceivedRequestFMDFlow):Component(Instruction):substance-declaration') }}
                    </InstructionText>
                    <Overlay :show="fmdLoading">
                        <FMDEditor
                            class="simple-flow-step-2"
                            :productId="productId"
                            :declarations="fmdDeclarations"
                            :get-substance-lists="fetchSharedFmdSubstanceLists"
                            @change="saveFMDDeclaration"
                            @editing-change="onFMDEditingChange"
                        />
                        <b-alert
                            v-if="fmdSaveErrorMessage"
                            show
                            variant="danger"
                            class="mt-1 mb-0"
                        >
                            <div class="alert-body d-flex flex-column flex-md-row align-items-md-center justify-content-between">
                                <div class="pr-md-1">
                                    <strong v-if="fmdSaveErrorTitle">{{ fmdSaveErrorTitle }}</strong>
                                    <div>{{ fmdSaveErrorMessage }}</div>
                                </div>
                                <b-button
                                    variant="outline-danger"
                                    size="sm"
                                    class="mt-1 mt-md-0"
                                    :disabled="fmdLoading"
                                    @click="retrySaveFMDDeclaration"
                                >
                                    {{ $t('Component(Button):save') }}
                                </b-button>
                            </div>
                        </b-alert>
                    </Overlay>
                    <b-form-checkbox
                        v-if="shouldShowGuaranteeCheckbox"
                        v-model="guaranteedWeightPercentageConfirmed"
                        class="mt-1"
                    >
                        {{ $t('Page(ReceivedRequestFMDFlow):Component(Label):guaranteed-weight-percentage-confirmation', { guaranteedWeightPercentage: request.guaranteedWeightPercentage }) }}
                    </b-form-checkbox>
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            @click="() => doneStep(2)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible('substances-lists')"
                    :disabled="isSubstanceListsStepDisabled"
                    :title="$t('Page(ReceivedRequestFMDFlow):Component(StepItem):step-substance-lists-label', { guaranteedWeightPercentage: request.guaranteedWeightPercentage })"
                    :class="getStepVariantStyles(3).class"
                    :header-bg-variant="getStepVariantStyles(3).bg"
                    :header-text-variant="getStepVariantStyles(3).text"
                    ref="step3"
                >
                    <SubstanceListsWithQuestions
                        :key="substanceListsStepLegislationsKey"
                        class="simple-flow-step-3"
                        v-if="renderStage > 1"
                        ref="ConcernElementsComponent"
                        :request="request"
                        :product="product"
                        :legislations="substanceListsStepLegislations"
                        :included-substances="fmdIncludedSubstances"
                        :included-list-ids="fmdIncludedListIds"
                        :loading="fmdLoading || fmdIncludedListsLoading"
                        :collapse-questionless-referenced-lists="true"
                        @validityChanged="(isValid) => changeStepValidity(3, isValid)"
                        @listQuestionStateUpdated="listQuestionStateUpdated"
                    />
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            @click="() => doneStep(3)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible('verification')"
                    :title="$t('Page(ReceivedRequestFMDFlow):Component(StepItem):step-verification-documents-label')"
                    :class="getStepVariantStyles(4).class"
                    :header-bg-variant="getStepVariantStyles(4).bg"
                    :header-text-variant="getStepVariantStyles(4).text"
                    ref="step4"
                >
                    <InstructionText class="simple-flow-instruction">
                        {{ $t('Page(ReceivedRequestFMDFlow):Component(Instruction):verification-documents') }}
                    </InstructionText>
                    <Overlay :show="showDocumentLoading">
                        <Verification
                            class="simple-flow-step-4"
                            ref="verification"
                            :product="product"
                            :id="productId"
                            :visible-elements="verificationDocuments"
                            :compact-view="verificationCompactView"
                            :evidence="evidenceReports"
                            :compliant-statement="compliantStatement"
                            :compliant-statement-border-variant="compliantStatementStyle.border"
                            :evidence-report-border-variant="evidenceReportStyle.border"
                            :manage-predefined="false"
                            :default-compliant-statement-legislation-list="requestedLegislationIds"
                            :default-evidence-report-legislation-list="requestedLegislationIds"
                            @addCompliantStatement="addCompliantStatement"
                            @removeCompliantStatement="removeCompliantStatement"
                            @compliantStatementChange="documentUpdated"
                            @evidenceReportChange="documentUpdated"
                            @addReport="addReport"
                            @removeReport="removeEvidenceReport"
                        />
                    </Overlay>
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            @click="() => doneStep(4)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    v-if="isStepVisible('other-information')"
                    :title="$t('Page(ReceivedRequestFMDFlow):Component(StepItem):step-other-information-label')"
                    :class="getStepVariantStyles(5).class"
                    :header-bg-variant="getStepVariantStyles(5).bg"
                    :header-text-variant="getStepVariantStyles(5).text"
                    ref="step5"
                >
                    <Overlay :show="otherReplyInformationLoading">
                        <InstructionText class="simple-flow-instruction">
                            <span v-html="$t('Page(ReceivedRequestFMDFlow):Component(Instruction):other-information')"/>
                        </InstructionText>

                        <OtherReplyInformation
                            class="simple-flow-step-5"
                            :request="request"
                            @validityChange="(isValid) => changeStepValidity(5, isValid)"
                            @input="onOtherInformationChange"
                        />

                        <div class="text-center">
                            <DoneGoToNextStep
                                class="mt-1"
                                :disabled="!isStepValid(5)"
                                @click="() => doneStep(5)"
                            />
                        </div>
                    </Overlay>
                </app-collapse-item>
                <app-collapse-item
                    :title="$t('Page(ReceivedRequestFMDFlow):Component(StepItem):step-approve-declaration')"
                    v-if="isStepVisible('approve-declaration') && isApproveDeclarationStepVisible"
                    :disabled="isApproveDeclarationStepDisabled"
                    :class="getStepVariantStyles(6).class"
                    :header-bg-variant="getStepVariantStyles(6).bg"
                    :header-text-variant="getStepVariantStyles(6).text"
                    ref="step6"
                >
                    <InstructionText class="simple-flow-instruction">
                        <span v-html="$t('Page(ReceivedRequest):Component(Instruction):approve-declaration')"/>
                    </InstructionText>
                    <DeclarationApproval
                        class="no-border simple-flow-step-6"
                        label=""
                        :request-id="id"
                        @confirmed="declarationApproved"
                    />
                </app-collapse-item>
                <app-collapse-item
                    v-if="isFinalStepVisible"
                    :disabled="!isReplyStepActive"
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-final')"
                    ref="stepSendReply"
                >
                    <SendReply
                        :reply-details="request.replyDetails"
                        @send="sendReply"
                    />
                </app-collapse-item>
            </app-collapse>
        </b-card>
    </b-overlay>
</template>

<script>
import { BAlert, BButton, BCard, BRow, BCol, BOverlay, BFormCheckbox } from 'bootstrap-vue'
import {Actions} from "@/views/request/received/components";
import StatusBadge from "@/views/request/components/StatusBadge";
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import {ProductCardMixins} from "@/views/product/components/product-card";
import {ProductType, VerificationDocumentType} from "@/libs/product";
import DeclarationApproval from "@/views/request/received/components/DeclarationApproval";
import {
    ProductInformation, OtherReplyInformation,
} from "@/views/request/received/flow/components/fmd";
import {
    ViewRequest, ScipInformation,
    ProductComponentsDefinition, ManufacturingOriginDeclaration
} from "@/views/request/received/flow/components/vertiacal-steps";
import {Verification} from "@/views/product/components/product-card";
import SendReply from "@/views/request/received/flow/components/vertiacal-steps/SendReply";
import verticalStepsMixin from "@/views/request/received/flow/vertical-steps-mixin";
import DoneGoToNextStep from "@/views/request/received/flow/components/vertiacal-steps/DoneGoToNextStep";
import CustomCodes from "@/views/request/received/flow/components/vertiacal-steps/CustomCodes";
import {ProductProvider, receivedRequestProvider} from "@core/domain/product";
import InstructionText from "@core/components/common/instructions/InstructionText.vue";
import Overlay from "@core/components/common/Overlay.vue";
import RequestLifeCycle from "@/views/request/components/RequestLifeCycle.vue";
import {FMDEditor, SubstanceListsWithQuestions} from "@/views/product/components/product-card/fmd";
import FmdDerivedSubstanceListsMixin from "@/views/product/components/product-card/fmd/mixins/FmdDerivedSubstanceListsMixin";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import backendResponse from "@core/mixins/ui/backend-response";
import {LegislationKey} from "@core/domain/legislation";
import {getAllSubstanceLists} from "@core/domain/substance";

export default {
    mixins: [verticalStepsMixin, backendResponse, FmdDerivedSubstanceListsMixin],
    components: {
        RequestLifeCycle, Overlay, InstructionText, BAlert, BButton, BFormCheckbox,
        BCard, BRow, BCol, Actions, ViewRequest, ProductInformation, ScipInformation, Verification,
        BOverlay, AppCollapse, AppCollapseItem, StatusBadge,
        OtherReplyInformation, DeclarationApproval, SendReply, ProductComponentsDefinition, DoneGoToNextStep,
        CustomCodes, ManufacturingOriginDeclaration, FMDEditor, SubstanceListsWithQuestions
    },
    props: {
        id: {
            required: true,
        }
    },
    data() {
        return {
            renderStage: 1,
            verificationDocuments: ['compliant_statement', 'evidence'],
            validSteps: [],
            doneClickedSteps: [],

            otherInformation: null,
            isReplyDisabled: true,
            isReplyButtonVisible: false,
            isSaveButtonVisible: false,
            productId: null,

            fmdLoading: false,
            saveFMDDeclarationTimeout: null,
            fmdSaveErrorTitle: '',
            fmdSaveErrorMessage: '',
            saveDocumentTimeout: null,
            showDocumentLoading: false,

            saveProductInformationTimeout: null,
            saveListQuestionStateTimeout: null,
            productInformationLoading: false,

            verificationCompactView: false,
            concernElementsCompactView: false,

            requestedLegislationIds: [],

            resizeObserver: null,

            ...ProductCardMixins.data,
        };
    },
    methods: {
        saveFMDDeclaration(event) {
            if (event.hasUnfilledRows || event.isWeightEditing) {
                return;
            }

            this.onFMDChange(event);

            clearTimeout(this.saveFMDDeclarationTimeout);

            this.saveFMDDeclarationTimeout = setTimeout(() => {
                this.persistFMDDeclaration();
            }, 1000);
        },
        onFMDEditingChange(event) {
            if (!event || event.isWeightEditing) {
                return;
            }
            this.saveFMDDeclaration(event);
        },
        retrySaveFMDDeclaration() {
            if (!this.fmdSaveErrorMessage || this.fmdLoading) {
                return;
            }
            clearTimeout(this.saveFMDDeclarationTimeout);
            this.persistFMDDeclaration();
        },
        persistFMDDeclaration() {
            this.fmdLoading = true;
            return ProductProvider(this.getProductId())
                .saveFMD()
                .then(() => {
                    this.clearFMDSaveError();
                    return this.fetchSubstances(true);
                })
                .catch((error) => {
                    const backendError = this.getFMDSaveErrorDetails(error);
                    this.fmdSaveErrorTitle = backendError.title;
                    this.fmdSaveErrorMessage = backendError.message;
                    this.handleBackendError(error);
                })
                .finally(() => this.fmdLoading = false);
        },
        clearFMDSaveError() {
            this.fmdSaveErrorTitle = '';
            this.fmdSaveErrorMessage = '';
        },
        getFMDSaveErrorDetails(error) {
            if (error && error.response) {
                if (error.response.data instanceof Object && error.response.data.detail) {
                    return {
                        title: error.response.data.title || this.$t('Component(Text):validation-failed'),
                        message: error.response.data.detail,
                    };
                }
                if (error.response.status === 422 && error.response.data instanceof Array) {
                    return {
                        title: this.$t('Component(Text):validation-failed'),
                        message: error.response.data.map(violation => violation.message).join(' '),
                    };
                }
                if (error.response.status === 404) {
                    return {
                        title: this.$t('Component(Text):validation-failed'),
                        message: 'Requested resource not found',
                    };
                }
            }
            return {
                title: this.$t('Component(Text):validation-failed'),
                message: 'Unable to save declarations. Please try again.',
            };
        },

        documentUpdated(document) {
            if (document.id || (!document.id && document.document)) {
                clearTimeout(this.saveDocumentTimeout);
                this.saveDocumentTimeout = setTimeout(() => {
                    this.showDocumentLoading = true;
                    ProductProvider(this.getProductId())
                        .saveVerificationDocuments()
                        .then(() => {
                            this.fetchDocuments();
                        })
                        .catch(this.handleBackendError)
                        .finally(() => this.showDocumentLoading = false);
                }, 2500);
            }
        },

        showToast(variant, message, icon = 'BellIcon') {
            this.$toast({
                component: ToastificationContent,
                props: {
                    title: this.$t('Component(NotificationToast):title'),
                    icon: icon,
                    text: message,
                    variant: variant
                },
            }, {
                position: 'bottom-right',
            });
        },

        productInformationChanged() {
            clearTimeout(this.saveProductInformationTimeout);
            this.saveProductInformationTimeout = setTimeout(() => {
                this.productInformationLoading = true;
                ProductProvider(this.getProductId())
                    .saveCard()
                    .then(() => {
                        // this.showToast('primary', this.$t('Component(NotificationToast):changes-saved'), 'CheckIcon');
                    })
                    .catch(this.handleBackendError)
                    .finally(() => this.productInformationLoading = false)
            }, 2500);
        },

        showLifeCycle() {
            this.$refs.requestLifeCycle.show();
        },

        removeEvidenceReport(document, index) {
            this.evidenceReports.splice(index, 1);
        },

        close() {
            this.$router.push({
                name: 'received-requests',
            });
        },

        getProductTitle() {
            if (this.product.names.length > 0) {
                return this.product.names[0].value;
            }

            return '';
        },
        ...ProductCardMixins.methods,
        listQuestionStateUpdated(list, state) {
            ProductCardMixins.methods.listQuestionStateUpdated.call(this, list, state);
            this.scheduleListQuestionStatesSave();
        },

        scheduleListQuestionStatesSave() {
            clearTimeout(this.saveListQuestionStateTimeout);
            this.saveListQuestionStateTimeout = setTimeout(() => {
                this.productInformationLoading = true;
                ProductProvider(this.getProductId())
                    .saveCard()
                    .catch(this.handleBackendError)
                    .finally(() => this.productInformationLoading = false);
            }, 1500);
        },

        //  we redeclare it to fetch fmd as it is fmd flow
        fetchSubstances(refresh = false) {
            this.fmdLoading = true;
            return ProductProvider(this.getProductId())
                .getFMDDeclarations(refresh)
                .then((declarations) => {
                    this.fmdDeclarations = declarations;
                    return this.syncFmdDerivedSubstanceLists(declarations)
                        .then(() => this.syncHiddenFmdSubstanceListQuestionStates());
                })
                .finally(() => this.fmdLoading = false);
        },

        syncHiddenFmdSubstanceListQuestionStates() {
            if (!this.guaranteedWeightPercentageConfirmed || !this.isEuCmrRequested) {
                return Promise.resolve(false);
            }

            return getAllSubstanceLists()
                .then((lists) => {
                    const requestedLegislations = this.request && Array.isArray(this.request.legislations)
                        ? this.request.legislations.map(legislation => legislation.id)
                        : [];
                    const requestedLists = this.filterActiveSubstanceLists(lists, requestedLegislations);
                    const visibleLists = this.filterActiveSubstanceLists(lists, [LegislationKey.EU_CMR]);
                    const visibleListIds = visibleLists.map(list => String(list.id));
                    const includedListIds = (this.fmdIncludedListIds || []).map(id => String(id));
                    let hasChanges = false;

                    requestedLists
                        .filter(list => list.hasQuestion())
                        .filter(list => !visibleListIds.includes(String(list.id)))
                        .forEach(list => {
                            const effectiveListIds = [list.id]
                                .concat(list.getReferencingLists())
                                .map(id => String(id));
                            const state = effectiveListIds.some(id => includedListIds.includes(id));

                            if (this.updateProductListQuestionStateIfChanged(list, state)) {
                                hasChanges = true;
                            }
                        });

                    if (hasChanges) {
                        this.scheduleListQuestionStatesSave();
                    }

                    return hasChanges;
                })
                .catch((error) => {
                    this.handleBackendError(error);
                    return false;
                });
        },

        filterActiveSubstanceLists(lists, legislations) {
            if (!Array.isArray(legislations) || legislations.length === 0) {
                return lists;
            }

            const idListMap = {};
            lists.forEach(list => idListMap[list.getId()] = list);

            const filtered = lists
                .filter(list => list.key !== 'empty')
                .filter(list => list.hasIntersectionWithLegislations(legislations));
            const idList = filtered.map(list => list.id);

            filtered
                .filter(list => list.hasReferencingLists())
                .forEach(list => {
                    list.getReferencingLists().forEach(listId => {
                        if (!idList.includes(listId) && idListMap[listId]) {
                            idList.push(listId);
                            filtered.push(idListMap[listId]);
                        }
                    });
                });

            return filtered;
        },

        updateProductListQuestionStateIfChanged(list, state) {
            const product = ProductProvider(this.getProductId());
            if (product.card && !Array.isArray(product.card.substanceListQuestionStates)) {
                this.$set(product.card, 'substanceListQuestionStates', []);
            }

            const questionStates = product.card && Array.isArray(product.card.substanceListQuestionStates)
                ? product.card.substanceListQuestionStates
                : [];
            const currentState = questionStates
                .find(questionState => String(questionState.list) === String(list.id));

            if (currentState && currentState.state === state) {
                return false;
            }

            if (currentState) {
                currentState.state = state;
            } else {
                product.updateListQuestionState({id: list.id, title: list.title}, state);
            }

            if (this.product && Array.isArray(this.product.substanceListQuestionStates)) {
                const localState = this.product.substanceListQuestionStates
                    .find(questionState => String(questionState.list) === String(list.id));

                if (localState) {
                    localState.state = state;
                } else {
                    this.product.substanceListQuestionStates.push({
                        list: list.id,
                        state: state,
                    });
                }
            }

            return true;
        },

        fetchComponentsConcernElements() {
            //  do nothing as we do not need components concern elements here
        },

        getProductId() {
            return this.request ? this.request.product.id : null;
        },

        changeStepValidity(step, isValid) {
            if (isValid && !this.validSteps.includes(step)) {
                this.validSteps.push(step);
            } else if (!isValid && this.validSteps.includes(step)) {
                this.validSteps.splice(this.validSteps.findIndex(s => s === step), 1);
            }
        },

        gotoNextStep(step) {
            if (step === 6) {
                if (this.isApproveDeclarationStepVisible && !this.isApproveDeclarationStepDisabled) {
                    this.$refs.step6.updateVisible();
                } else {
                    this.$refs.stepSendReply.updateVisible();
                }
            } else {
                if (6 === step) {
                    this.$refs.stepSendReply.updateVisible();
                    return;
                }
                this.$refs['step' + step].updateVisible();
            }
        },

        onStepVisible(step) {
            if (this.isStepVisible('verification')) {
                this.managePredefinedDocuments();
            }
        },

        doneStep(step) {
            if (!this.doneClickedSteps.includes(step)) {
                this.doneClickedSteps.push(step);
            }
            const skipSubstanceListsStep = step === 2 && this.isSubstanceListsStepDisabled;
            const nextStep = skipSubstanceListsStep ? 4 : step + 1;
            const isVisible = this.isStepVisible(nextStep);
            this.markStepAsDone(step);
            if (skipSubstanceListsStep) {
                this.markStepAsDone(3);
            }
            if (isVisible) {
                this.gotoNextStep(nextStep);
                setTimeout(() => {
                    this.onStepVisible(nextStep);
                    this.smoothScroll(nextStep);
                }, 200);
            } else {
                setTimeout(() => {
                    this.gotoNextStep(nextStep);
                    setTimeout(() => {
                        this.onStepVisible(nextStep);
                        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                    }, 600);
                }, 200);
            }
        },

        smoothScroll(step) {
            document.querySelector('.simple-flow-step-' + step).scrollIntoView({
                behavior: 'smooth'
            });
        },

        isStepValid(step) {
            return this.validSteps.includes(step) || this.doneSteps.includes(step);
        },

        isStepVisible(step) {
            switch (step) {
                case 4:
                case 'verification':
                    return this.doneSteps.includes(3) || this.doneSteps.includes(4);
                case 6:
                case 'approve-declaration':
                    return this.doneSteps.includes(5);
                case 5:
                case 'other-information':
                    return this.doneSteps.includes(4) || this.doneSteps.includes(5);
                case 3:
                case 'substances-lists':
                    return this.doneSteps.includes(2) || this.doneSteps.includes(3);
                case 2:
                case 'ce':
                    return this.doneSteps.includes(1) || this.doneSteps.includes(2);
            }
            return false;
        },

        getStepVariantStyles(step) {
            if (8 === step) {   // approve declaration
                return this.isStepValid(step)
                ? {bg: 'default', text: 'default', class: 'done'}
                : {bg: 'default', text: 'default', class: ''};
            }

            return this.isStepValid(step) && this.doneSteps.includes(step)
                ? {bg: 'default', text: 'default', class: 'done'}
                : {bg: 'default', text: 'default', class: ''};
        },
        onResize() {
            this.verificationCompactView = this.$refs.verticalStepComponent.offsetWidth < 1500;
            this.concernElementsCompactView = this.$refs.verticalStepComponent.offsetWidth < 1145;
        },

        managePredefinedDocuments() {
            if (!this.isStepVisible('verification')) {
                return;
            }

            //  HAM-510

            //  Reports
            if (this.request.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                if (this.request.evidenceReportTypes.length > 0) {
                    const requestedTypes = this.request.evidenceReportTypes.reduce((requestedTypes, type) => {
                        if (!requestedTypes.hasOwnProperty(type.id)) {
                            requestedTypes[type.id] = type;
                        }
                        return requestedTypes;
                    }, {});
                    this.evidenceReports
                        .forEach((document) => {
                            document.documentTypes.map(type => {
                                if (requestedTypes.hasOwnProperty(type.id)) {
                                    delete requestedTypes[type.id];
                                }
                            });
                        })
                    ;

                    Object.keys(requestedTypes).forEach((key) => {
                        const report = this.$refs.verification.makeEvidenceReport();
                        report.documentTypes.push(requestedTypes[key]);
                        this.addReport(report);
                    });
                } else if (this.evidenceReports.length === 0) {
                    this.addReport(this.$refs.verification.makeEvidenceReport());
                }
            }

            if (
                this.request.verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)
                && this.compliantStatement.length === 0
            ) {
                this.addCompliantStatement(this.$refs.verification.makeCompliantStatement());
            }
        },
        fetched() {
            this.renderStage = 2;
            this.requestedLegislationIds = this.request.legislations.map(l => l.id);
        }
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.$refs.verticalStepComponent);
    },
    computed: {
        totalDeclaredMaximumWeightPercentage() {
            return (this.fmdDeclarations || []).reduce((sum, declaration) => {
                const weight = declaration && declaration.weight ? declaration.weight : {};
                const rawValue = weight.to;
                const value = rawValue === null || rawValue === undefined || rawValue === '' ? 0 : Number(rawValue);
                return sum + (Number.isNaN(value) ? 0 : value);
            }, 0);
        },
        shouldShowGuaranteeCheckbox() {
            const guaranteedWeightPercentage = Number(this.request && this.request.guaranteedWeightPercentage);
            if (Number.isNaN(guaranteedWeightPercentage)) {
                return false;
            }
            return this.totalDeclaredMaximumWeightPercentage >= guaranteedWeightPercentage;
        },
        isEuCmrRequested() {
            return Boolean(
                this.request
                && Array.isArray(this.request.legislations)
                && this.request.legislations.some(legislation => legislation.key === LegislationKey.EU_CMR)
            );
        },
        isSubstanceListsStepDisabled() {
            return this.guaranteedWeightPercentageConfirmed && !this.isEuCmrRequested;
        },
        substanceListsStepLegislations() {
            if (this.guaranteedWeightPercentageConfirmed && this.isEuCmrRequested) {
                return [LegislationKey.EU_CMR];
            }

            return this.request && Array.isArray(this.request.legislations)
                ? this.request.legislations.map(legislation => legislation.id)
                : [];
        },
        substanceListsStepLegislationsKey() {
            return this.substanceListsStepLegislations.join('-');
        },
        guaranteedWeightPercentageConfirmed: {
            get() {
                return Boolean(this.request && this.request.guaranteedWeightPercentageConfirmed);
            },
            set(value) {
                const normalized = Boolean(value);
                if (this.request) {
                    this.request.guaranteedWeightPercentageConfirmed = normalized;
                }

                if (this.request && this.request.id) {
                    const request = receivedRequestProvider(this.request.id);
                    request.setGuaranteedWeightPercentageConfirmed(normalized);
                    this.saveReceivedRequest();
                    this.syncHiddenFmdSubstanceListQuestionStates();
                }
            }
        },
        isFinalStepVisible() {
            //  if last declarative step visible then it should be visible
            if (!this.isApproveDeclarationStepVisible) {
                return this.isStepVisible('other-information');
            }
            return this.isStepVisible('approve-declaration');
        },
        doneSteps() {
            return this.request.doneSteps ? this.request.doneSteps : [];
        },
        firstStepStyle() {
            return this.getStepVariantStyles(1);
        },
        isVerificationStepFullFiled() {
            const verificationDocuments = this.request.verificationDocuments;
            if (verificationDocuments.length === 0) {
                return true;
            }

            let states = [];
            if (verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                states.push(!(this.evidenceReports.length === 0));
                //  by default we add one empty document
                if (this.evidenceReports.length === 1 && !this.evidenceReports[0].id) {
                    states.push(false);
                }
            }

            if (verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)) {
                states.push(!(this.compliantStatement.length === 0));
                //  by default we add one empty document
                if (this.compliantStatement.length === 1 && !this.compliantStatement[0].id) {
                    states.push(false);
                }
            }

            const state = states.includes(false) === false;
            this.changeStepValidity(3, state);
            return state;
        },
        evidenceReportStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                state = 'default';
            } else if (this.evidenceReports.length && this.evidenceReports[0].id) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
        compliantStatementStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)) {
                state = 'default';
            } else if (this.compliantStatement.length && this.compliantStatement[0].id) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
    },
    watch: {
        isSubstanceListsStepDisabled(disabled, wasDisabled) {
            if (disabled && this.$refs.step3) {
                this.$refs.step3.visible = false;
                return;
            }

            if (wasDisabled && !disabled && this.isStepVisible('substances-lists') && this.$refs.step3) {
                this.$nextTick(() => {
                    this.$refs.step3.updateVisible(true);
                    setTimeout(() => this.smoothScroll(3), 200);
                });
            }
        },
        shouldShowGuaranteeCheckbox(next) {
            if (!next && this.guaranteedWeightPercentageConfirmed) {
                this.guaranteedWeightPercentageConfirmed = false;
            }
        },
        evidenceReports() {
            this.managePredefinedDocuments();
        }
    }
}
</script>
<style lang="scss">
#simple-flow {
    color: black;
    .card-title, .reply-process-heading, .table, h6, label, h2, .simple-flow-instruction {
        color: black;
    }
    .verification-component .card-header {
        h4 {
            font-size: 1rem;
        }
        padding: 0.5rem 0 0.5rem 1rem;
    }
}
.vertical-steps-flow > .card {
    border: 1px solid #d5d3da !important;
}
.vertical-steps-flow > .card.done {
    border-color: #21b764b3 !important;

    background-position: right 3rem top 1.23rem;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%2321b764b3' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-size: calc(0.725em + 0.438rem) calc(0.725em + 0.438rem);
}
.collapse-margin .card.border-success {
    border-bottom: 1px solid #21b764b3 !important;
}
.collapse-margin .card.border-warning {
    border-bottom: 1px solid #ffdb3c !important;
}
.vertical-steps-flow .card {
    .component-concern-elements > .card {
        border: 1px solid #d5d3da !important;
    }

    .card.component-concern-elements.has-concern-elements:not(.collapsed) > .card-title {
        border-bottom: 1px solid #d5d3da;
    }

    .card.component-concern-elements.has-concern-elements.collapsed > .card-title {
        border-bottom: none;
        margin-bottom: 0;
    }

    .card.component-concern-elements.no-concern-elements > .card-title {
        margin-bottom: 0;
    }

    .card:not(.border-success):not(.border-warning) {
        border: 1px solid rgba(34, 41, 47, 0.125) !important;
    }

    .document-row:last-child {
        margin-bottom: 2REM;
    }
}
</style>
